/**
 * The owner Dokumente page's drill-down (COL-628), as pure functions.
 *
 *   Alle · Allgemein · Objektbezogen ▾ (unit)
 *                        └ Wohnungsbezogen · Zimmer ▾ (room)
 *                                             └ Zimmerbezogen · Mieter ▾ (tenancy)
 *
 * The selection is a path, not a set of flags: picking a unit lands on its
 * Wohnungsbezogen files, picking a room lands on its Zimmerbezogen files, and
 * picking a tenancy shows that tenancy's files. Going back up a level is
 * clearing everything below it, so "which sub-pill is active" never has to be
 * stored separately from where you are.
 *
 * Everything is placed by id (apartment_id / room_id / booking_id from
 * owner_portfolio_documents). Room titles repeat across a building's units
 * ("Zimmer 1"), so a label could not place a tenant's own upload.
 *
 * The lists offered at each level are built from the documents themselves, so
 * a unit, room or tenancy appears only once it holds at least one file.
 */
import type { OwnerPortfolioDocument } from "@/hooks/owner/useOwnerPortfolioDocuments";
import { tenantTimeline } from "@/lib/tenant-timeline";

export type DocumentTop = "all" | "general" | "object";

export type DocumentPath = {
  top: DocumentTop;
  /** A UnitOption key. Only meaningful under "object". */
  unit: string | null;
  room: number | null;
  /** A booking id. */
  tenancy: number | null;
};

export const ROOT_PATH: DocumentPath = {
  top: "all",
  unit: null,
  room: null,
  tenancy: null,
};

/**
 * A unit to drill into. Usually an apartment. A building whose rooms are not
 * assigned to any apartment (rooms.apartment_id is nullable), or which only
 * holds building-wide files, gets one unit of its own keyed by the property,
 * so none of its files becomes unreachable.
 */
export type UnitOption = {
  key: string;
  propertyId: number | null;
  propertyTitle: string | null;
  apartmentId: number | null;
  apartmentLabel: string | null;
};

export type RoomOption = { id: number; title: string | null };

/** past / current / future, by the Tenants page rule; `notice` is a current
 *  tenancy that already has its move-out date. */
export type TenancyPhase = "current" | "notice" | "future" | "past";

export type TenancyOption = {
  bookingId: number;
  name: string | null;
  moveIn: string | null;
  moveOut: string | null;
  phase: TenancyPhase;
};

const apartmentKey = (id: number) => `a:${id}`;
const propertyKey = (id: number | null) => `p:${id ?? "none"}`;

/** True for a file that sits on the building itself, not on any unit, room
 *  or tenancy inside it. */
function isBuildingWide(d: OwnerPortfolioDocument): boolean {
  return (
    d.level !== "general" &&
    d.apartment_id == null &&
    d.room_id == null &&
    d.booking_id == null
  );
}

/** The unit a file belongs to, or null when it belongs to the building as a
 *  whole (or to no building at all). */
function unitKeyOf(d: OwnerPortfolioDocument): string | null {
  if (d.level === "general") return null;
  if (d.apartment_id != null) return apartmentKey(d.apartment_id);
  if (d.room_id != null || d.booking_id != null)
    return propertyKey(d.property_id);
  return null;
}

export function byLabel(a: string | null, b: string | null): number {
  return (a ?? "").localeCompare(b ?? "", "de", { numeric: true });
}

export function unitOptions(docs: OwnerPortfolioDocument[]): UnitOption[] {
  const units = new Map<string, UnitOption>();
  const propertiesWithUnits = new Set<number | null>();
  for (const d of docs) {
    const key = unitKeyOf(d);
    if (!key || units.has(key)) continue;
    units.set(key, {
      key,
      propertyId: d.property_id,
      propertyTitle: d.property_title,
      apartmentId: d.apartment_id ?? null,
      apartmentLabel: d.apartment_id != null ? d.apartment_label : null,
    });
    propertiesWithUnits.add(d.property_id);
  }
  // A building that holds only building-wide files still needs a way in.
  for (const d of docs) {
    if (!isBuildingWide(d) || propertiesWithUnits.has(d.property_id)) continue;
    const key = propertyKey(d.property_id);
    if (units.has(key)) continue;
    units.set(key, {
      key,
      propertyId: d.property_id,
      propertyTitle: d.property_title,
      apartmentId: null,
      apartmentLabel: null,
    });
  }
  return [...units.values()].sort(
    (a, b) =>
      byLabel(a.propertyTitle, b.propertyTitle) ||
      byLabel(a.apartmentLabel, b.apartmentLabel)
  );
}

export function roomOptions(
  docs: OwnerPortfolioDocument[],
  unitKey: string
): RoomOption[] {
  const rooms = new Map<number, RoomOption>();
  for (const d of docs) {
    if (d.room_id == null || unitKeyOf(d) !== unitKey) continue;
    if (!rooms.has(d.room_id)) {
      rooms.set(d.room_id, { id: d.room_id, title: d.room_title });
    }
  }
  return [...rooms.values()].sort((a, b) => byLabel(a.title, b.title));
}

export function tenancyPhase(
  d: Pick<
    OwnerPortfolioDocument,
    | "tenancy_status"
    | "tenancy_move_in"
    | "tenancy_move_out"
    | "tenancy_archived_at"
  >,
  today: string
): TenancyPhase {
  const timeline = tenantTimeline(
    {
      status: d.tenancy_status ?? null,
      start_date: d.tenancy_move_in ?? null,
      end_date: d.tenancy_move_out ?? null,
      admin_archived_at: d.tenancy_archived_at ?? null,
    },
    today
  );
  // null is a booking that never became a tenancy (cancelled, archived…).
  // Its files are history, so it sorts with the past.
  if (timeline === null || timeline === "past") return "past";
  if (timeline === "future") return "future";
  return d.tenancy_move_out ? "notice" : "current";
}

const PHASE_ORDER: Record<TenancyPhase, number> = {
  current: 0,
  notice: 0,
  future: 1,
  past: 2,
};

/** Active first, then upcoming, then past; newest move-in first in each. */
export function tenancyOptions(
  docs: OwnerPortfolioDocument[],
  roomId: number,
  today: string
): TenancyOption[] {
  const tenancies = new Map<number, TenancyOption>();
  for (const d of docs) {
    if (d.room_id !== roomId || d.booking_id == null) continue;
    if (tenancies.has(d.booking_id)) continue;
    tenancies.set(d.booking_id, {
      bookingId: d.booking_id,
      name: d.tenant_name,
      moveIn: d.tenancy_move_in ?? null,
      moveOut: d.tenancy_move_out ?? null,
      phase: tenancyPhase(d, today),
    });
  }
  return [...tenancies.values()].sort(
    (a, b) =>
      PHASE_ORDER[a.phase] - PHASE_ORDER[b.phase] ||
      (b.moveIn ?? "").localeCompare(a.moveIn ?? "")
  );
}

/** The files shown for a path, before the search box narrows them. */
export function documentsAtPath(
  docs: OwnerPortfolioDocument[],
  path: DocumentPath,
  units: UnitOption[]
): OwnerPortfolioDocument[] {
  if (path.top === "all") return docs;
  if (path.top === "general") return docs.filter((d) => d.level === "general");
  if (!path.unit) return docs.filter((d) => d.level !== "general");

  if (path.tenancy != null) {
    return docs.filter((d) => d.booking_id === path.tenancy);
  }
  if (path.room != null) {
    return docs.filter((d) => d.room_id === path.room && d.booking_id == null);
  }
  // Wohnungsbezogen: the unit's own files, plus its building's building-wide
  // files — the owner reads an Energieausweis as part of every unit in it.
  const unit = units.find((u) => u.key === path.unit);
  return docs.filter((d) => {
    if (isBuildingWide(d))
      return unit != null && d.property_id === unit.propertyId;
    return (
      unitKeyOf(d) === path.unit && d.room_id == null && d.booking_id == null
    );
  });
}
